/**
 * Auth-expiry error detection for React Router error boundaries.
 *
 * The Worker-level handler in `apps/react/workers/app.ts` already redirects
 * unauthenticated full-page loads to `/login`. This helper detects auth-expiry
 * errors that surface to the client-side `ErrorBoundary` instead — typically
 * from loaders/actions that throw a `Response` with status 401, or Stytch SDK
 * calls that reject with a recognized session-expired signal.
 *
 * See `apps/react/app/lib/stytch-errors.ts#is_stale_session_error` for the
 * pattern this mirrors.
 */

import { isRouteErrorResponse } from "react-router";

/**
 * Returns `true` when the given error indicates the user's session has
 * expired (or is otherwise unauthenticated) and we should surface the
 * "please log back in" view instead of the generic error fallback.
 */
export function is_auth_expired_error(error: unknown): boolean {
  // Loader/action threw a Response with status 401.
  if (isRouteErrorResponse(error) && error.status === 401) {
    return true;
  }

  // Plain Error with a message that smells like an auth-expiry signal.
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    if (matches_auth_expired_message(message)) return true;
  }

  // Stytch SDK errors and similar plain-object errors with an
  // `error_type` / `error_message` shape.
  if (error && typeof error === "object") {
    const e = error as Record<string, unknown>;

    if (typeof e.error_type === "string") {
      const error_type = e.error_type.toLowerCase();
      if (
        error_type.includes("session_not_found") ||
        error_type.includes("session_expired") ||
        error_type.includes("unauthorized") ||
        error_type.includes("unauthenticated")
      ) {
        return true;
      }
    }

    const stytch_message =
      typeof e.error_message === "string"
        ? e.error_message.toLowerCase()
        : typeof e.message === "string"
        ? e.message.toLowerCase()
        : "";
    if (stytch_message && matches_auth_expired_message(stytch_message)) {
      return true;
    }
  }

  return false;
}

function matches_auth_expired_message(message: string): boolean {
  // "session" + "expired" / "invalid" / "not found"
  if (
    message.includes("session") &&
    (message.includes("expired") ||
      message.includes("invalid") ||
      message.includes("not found"))
  ) {
    return true;
  }
  if (message.includes("unauthenticated")) return true;
  if (message.includes("unauthorized")) return true;
  return false;
}
